import * as React from "react";
import {cx} from 'emotion';

import "./user-information-menu.scss";

import {cs}                     from "@Common/react/chain-services";
import {consumeContext}         from "@Common/react/context";
import {scopedState}            from "@Common/react/scoped-state";
import {UseState}               from "@Common/react/use-state";
import {setNestedValue}         from "@Common/utils/arr-path";
import {equalDeep, oMapToArr}   from "@Common/utils/objects";
import {COMPANY_LEVEL}          from "@Xpresso/routes/common/logic/user-level";
import {OFFICE_LEVEL}           from "@Xpresso/routes/common/logic/user-level";

import {MenuLayout}          from "../menu-layout/menu-layout";
import {LayoutLoaderContext} from "../../../../user-assets/common/layout/user-assets-layout-loader";

export const UserInformationMenu = ({agent, readOnly, dataUpdated}) => cs(
    consumeContext("apis"),
    consumeContext("session"),
    consumeContext("company"),
    consumeContext("userLevels"),
    ["uploading", (_, next) => UseState({next})],
    ["agentFields", (_, next) => next(getAgentFields())],
    ["userInformationState", ({agentFields}, next) => agent.value && UseState({
        _key: "userInformation",
        initValue: defaultState({agent: agent.value, agentFields}),
        next
    })],
    ["loader", (_, next) => (
        <LayoutLoaderContext.Consumer>
            {next}
        </LayoutLoaderContext.Consumer>
    )],
    ["headerData", ({loader}, next) => loader.loadHeaderData({agentId: null, next})],
    ({apis, uploading, userInformationState, agentFields}, next) => MenuLayout({
        title: "User Information",
        button: readOnly?.canEdit && {
            onClick: async () => {
                uploading.onChange(true);
                const result = await apis.next.session.updateAgent({updateFields: userInformationState.value.inputs});
                if (result?.errorMessage) {
                    alert(result.errorMessage);
                } else if (result?.errorInfo) {
                    const errors = JSON.parse(result.errorInfo);
                    errors.map((error) => {
                        scopedState(userInformationState, ['errors', error.mapping]).onChange(error.message)
                    });
                } else {
                    dataUpdated.onChange(false);
                    await agent.reload();
                }
                uploading.onChange(false);
            },
            disabled: readOnly?.inactive || equalDeep(defaultState({agent: agent.value, agentFields}), userInformationState.value) ||
                      uploading.value || missingRequiredData({userInformationState}),
        },
        next,
    }),
    ({userInformationState, agentFields, company, session, userLevels}) => (
        <div className="user-information-menu-l3o">
            {agentFields.map((field) => (
                <div className="input-card" key={field.mapping}>
                    <div className="label">
                        {field.label}
                        {requiredFields.includes(field.mapping) && <span style={{color: "red"}}>{" *"}</span>}
                    </div>
                    <input {...{
                        readOnly: readOnly?.inactive || !readOnly?.canEdit || !editAllowed(company, field, session, userLevels),
                        value: scopedState(userInformationState, ['inputs', field.mapping]).value,
                        onChange: (e) => {
                            let value = e.target.value; 
                            scopedState(userInformationState, ['errors', field.mapping]).onChange(false);
                            scopedState(userInformationState, ['inputs', field.mapping]).onChange(value);
                            dataUpdated.onChange(
                                !equalDeep(defaultState({agent: agent.value, agentFields}), 
                                    setNestedValue(userInformationState.value, ['inputs', field.mapping], value)
                                )
                            );
                        },
                        className: cx("value", {
                            "read-only": readOnly?.inactive || !readOnly?.canEdit || !editAllowed(company, field, session, userLevels)
                        }),
                        maxLength: field.maxLength,
                        "xd-qa-type": "textbox",
                        "xd-qa-name": field.label,
                    }}/>
                    {userInformationState.value.errors[field.mapping] && (
                        <div className="errorMessage">{userInformationState.value.errors[field.mapping]}</div>
                    )}
                </div>
            ))}
        </div>
    )
);

const defaultState = ({agent, agentFields}) => { 
    let inputs = {idAgent: agent.idAgent};
    let errors = {};
    agentFields.map((datum) => {
        inputs[datum.mapping] = agent[datum.mapping];
        errors[datum.mapping] = false;
    });
    return {
        inputs,
        errors,
    }
};

const requiredFields = ["agentNameFirst", "agentNameLast", "agentPhone1", "agentEmail"];

const getAgentFields = () => ([
    {
        "label": "First Name", 
        "mapping": "agentNameFirst", 
        "maxLength": 50
    },
    {
        "label": "Middle Name", 
        "mapping": "agentNameMiddle", 
        "maxLength": 50
    },
    {
        "label": "Last Name",
        "mapping": "agentNameLast",
        "maxLength": 50
    },
    {
        "label": "Phone 1",
        "mapping": "agentPhone1", 
        "maxLength": 25
    },
    {
        "label": "Phone 2",
        "mapping": "agentPhone2",
        "maxLength": 25
    },
    {
        "label": "Email",
        "mapping": "agentEmail",
        "maxLength": 60
    },
    {
        "label": "Link ID",
        "mapping": "agentLinkId",
        "maxLength": 60,
        "checkPermissions": true,
    },
]);

const missingRequiredData = ({userInformationState}) => {
    return requiredFields.some((inputName) => !scopedState(userInformationState, ['inputs', inputName]).value) ||
        oMapToArr(scopedState(userInformationState, ['errors']).value).some((errorName) => errorName);
}
const editAllowed = (company, field, session, userLevels) => {
    if(!field.checkPermissions) {
        return true
    }
    switch(userLevels?.userLevel) {
        case COMPANY_LEVEL:
            return company?.prefs.allowEditAgentLinkID
        case OFFICE_LEVEL:
            return editAllowedOffice(company, session)
        default:
            return false
    }
}

const editAllowedOffice = (company, session) => {
    const NO = 0
    const YES = 1
    const USE_COMPANY_PREF = 2

    const pref = parseInt(session?.data?.branch?.prefs?.allowEditAgentLinkId) ?? USE_COMPANY_PREF
    switch(pref) {
        case YES:
            return true
        case NO:
            return false
        case USE_COMPANY_PREF:
        default:
            return company?.prefs.allowEditAgentLinkID            
    }
}
